<template>
  <div class="client-details" v-loading="loading.client">
    <quick-info-section>
      <el-row :gutter="18">
        <el-col :span="6">
          <el-card class="quick-info-box">
            <p class="ma-0">Early Bird: <early-bird-status-tag></early-bird-status-tag></p>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="quick-info-box">
            <p class="ma-0">Office: <office-tag :office-id="client.officeId"></office-tag></p>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="quick-info-box">
            <p class="ma-0">RAF Code: <el-tag>{{ client.rafCode }}</el-tag></p>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="quick-info-box">
            <p class="ma-0">Referrals: <el-tag>{{ referrals.length }}</el-tag></p>
          </el-card>
        </el-col>
      </el-row>
    </quick-info-section>
    <el-row :gutter="18">
      <el-col :md="hasSpouse ? 12 : 24" class="mb-3">
        <client-card :name="fullName" title="Primary" :ssn="client.ssn" :state-id="client.stateId">
          <template slot="actions">
            <el-button type="primary" icon="fa fa-user" v-if="!hasSpouse" @click="addSpouse">Add Spouse</el-button>
            <el-button type="primary" icon="fa fa-pencil-alt" @click="editPrimary">Edit</el-button>
          </template>
          <el-row :gutter="18">
            <el-col :sm="12" :lg="hasSpouse ? 12 : 6">
              <static-field label="Email" :value="client.email"></static-field>
            </el-col>
            <el-col :sm="12" :lg="hasSpouse ? 12 : 6">
              <static-field label="Phone" :value="client.phone"></static-field>
            </el-col>
            <el-col :sm="12" :lg="hasSpouse ? 12 : 6">
              <static-field label="Address" :value="address"></static-field>
            </el-col>
            <el-col :sm="12" :lg="hasSpouse ? 12 : 6">
              <static-field label="Lead Source" :value="client.leadSource"></static-field>
            </el-col>
            <el-col :sm="12" :lg="hasSpouse ? 12 : 6">
              <static-field label="Created" :value="dateAdded"></static-field>
            </el-col>
          </el-row>
        </client-card>
      </el-col>
      <el-col :md="12" v-if="hasSpouse" class="mb-3">
        <client-card :name="spouseName" title="Spouse" :ssn="spouse.ssn" :state-id="spouse.stateId"
          :class="{ 'disable-spouse': !hasSpouse }">
          <template slot="actions">
            <el-button type="primary" icon="fa fa-pencil-alt" @click="editSpouse">Edit</el-button>
            <el-button type="danger" icon="fa fa-ban" @click="separateClientSpouse" v-if="isAdmin">Separate
              Client/Spouse</el-button>
          </template>
          <el-row :gutter="18" v-if="hasSpouse">
            <el-col :sm="12">
              <static-field label="Email" :value="spouse.email"></static-field>
            </el-col>
            <el-col :sm="12">
              <static-field label="Phone" :value="spouse.phone"></static-field>
            </el-col>
            <el-col :sm="12">
              <static-field label="Address" :value="address"></static-field>
            </el-col>
            <el-col :sm="12">
              <static-field label="Created" :value="spouseDateAdded"></static-field>
            </el-col>
          </el-row>
        </client-card>
      </el-col>
    </el-row>
    <el-row :gutter="18">
      <el-col :md="24" class="mb-3">
        <el-card class="box-card" :body-style="{ padding: '0' }" v-loading="loading.paystubs">
          <div slot="header" class="clearfix detail-header">
            <i class="fa fa-clone"></i>Paystubs
            <el-button type="primary" icon="fa fa-plus" @click="addPaystub" v-if="!ebApplicationComplete">Add
              Paystub</el-button>
            <w2-pdf-button icon="fa fa-list-alt" class="mr-3" :disabled="paystubs.length == 0">View W-2s</w2-pdf-button>
          </div>
          <paystub-table :paystubs="paystubs" @edit="editPaystub" :read-only="ebApplicationComplete"></paystub-table>
        </el-card>
      </el-col>
    </el-row>
    <el-row :gutter="18">
      <el-col :md="24" class="mb-3">
        <el-card class="box-card" :body-style="{ padding: '0' }" v-loading="loading.earlyBird">
          <div slot="header" class="clearfix detail-header">
            <i class="fa fa-money-bill"></i>Early Bird Application <early-bird-status-tag></early-bird-status-tag>
            <el-button type="primary" icon="fa fa-money-bill" class="mr-3" @click="navigateToEarlyBirdApplication"
              v-if="!ebApplicationComplete">Start or Continue Application</el-button>
          </div>
          <h3 class="pa-3" v-if="ebApplicationEnrolled">
            <span :class="{ 'strike': ebEnrollment.transaction && ebEnrollment.transaction.void === true }">Client
              received
              $50 on {{ formatDate(ebEnrollment.dateAdded) }}.</span>
            <el-button v-if="isAdmin" type="danger" @click="removeEnrollment" size="small">Remove Enrollment</el-button>
            <el-button v-if="isAdmin" type="primary" @click="viewTransaction(ebEnrollment.transaction)" size="small"
              icon="fa fa-ticket">View Transaction</el-button>
          </h3>
          <h3 class="pa-3" v-if="ebApplicationComplete && !ebApplicationEnrolled && !ebOptout && isAdmin">
            {{ formattedReasonsDNQ }}
            <br />
            <el-button class="mt-3" type="primary" @click="overrideApprovalForEB">Override and Approve</el-button>
          </h3>
          <h3 class="pa-3" v-if="ebApplicationComplete && ebOptout">
            The client elected to OPT-OUT of the Early Bird Program. <el-button type="danger" @click="removeEbOptout"
              v-if="isAdmin">Remove Opt-Out</el-button>
          </h3>
        </el-card>
      </el-col>
    </el-row>
    <el-row :gutter="18">
      <el-col :md="24" class="mb-3">
        <el-card class="box-card" :body-style="{ padding: '0' }" v-loading="loading.referrals">
          <div slot="header" class="clearfix detail-header referral">
            <i class="fa fa-retweet"></i>Referrals <span>Code: {{ client.rafCode }}</span>
            <el-button type="primary" icon="fa fa-plus" @click="displayLogReferralDialog">Log Referral</el-button>
            <el-button class="mr-3" type="primary" icon="fa fa-tags" :disabled="!hasReferralsReadyForPayout"
              @click="navigateToPayReferrals">Pay Referrals</el-button>
            <raf-slips-pdf-button icon="fa fa-list-alt" :client-id="clientId">View RAF Slips</raf-slips-pdf-button>
          </div>
          <referral-table :referrals="referrals" @changeReferralOffice="onChangeReferralOffice"
            @overrideReferral="onOverrideReferral" @deleteReferral="onDeleteReferral" />
        </el-card>
      </el-col>
    </el-row>
    <el-dialog title="Edit Paystub" :visible.sync="editPaystubDialogVm.visible" width="600px">
      <edit-paystub-dialog :paystub="editPaystubDialogVm.paystub"
        @close="editPaystubDialogVm.visible = false"></edit-paystub-dialog>
    </el-dialog>
    <el-dialog title="Edit Client" :visible.sync="editClientDialogVm.visible" width="600px">
      <edit-client-dialog :client="editClientDialogVm.client" @close="editClientDialogVm.visible = false"
        :is-spouse="editClientDialogVm.isSpouse"></edit-client-dialog>
    </el-dialog>
    <el-dialog title="Log Referral" :visible.sync="logReferralDialogVm.visible" :show-close="false"
      :close-on-click-modal="false" :close-on-press-escape="false" width="50%">
      <log-referral-dialog :rafCode="logReferralDialogVm.rafCode" @close="logReferralDialogVm.visible = false"
        @addedReferral="onAddedReferral"></log-referral-dialog>
    </el-dialog>
    <el-dialog title="Transaction" :visible.sync="transactionDialogVm.visible" :show-close="false"
      :close-on-click-modal="false" :close-on-press-escape="false" width="400px">
      <transaction-dialog :transaction="transactionDialogVm.transaction" @close="transactionDialogVm.visible = false"
        @transactionUpdated="onTransactionUpdate"></transaction-dialog>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import get from 'lodash/get';
import moment from 'moment';
import api from '@/api';
import Client from '@/model/Client';
import Paystub from '@/model/Paystub';
import QuickInfoSection from '@/components/layout/QuickInfoSection.vue';
import StaticField from '@/components/widgets/StaticField.vue';
import ClientCard from '@/components/widgets/ClientCard.vue';
import PaystubTable from '@/components/tables/Paystub.vue';
import ReferralTable from '@/components/tables/Referral.vue';
import EditPaystubDialog from '@/components/dialog-content/EditPaystub.vue';
import EditClientDialog from '@/components/dialog-content/EditClient.vue';
import LogReferralDialog from '@/components/dialog-content/LogReferral.vue';
import TransactionDialog from '@/components/dialog-content/Transaction.vue';
import EarlyBirdStatusTag from '@/components/tags/EarlyBirdStatus.vue';
import OfficeTag from '@/components/tags/OfficeTag.vue';
import W2PdfButton from '@/components/PDFButton/W2';
import RafSlipsPdfButton from '@/components/PDFButton/RAFSlips';

export default {
  name: 'ClientDetail',
  components: {
    QuickInfoSection,
    StaticField,
    ClientCard,
    PaystubTable,
    ReferralTable,
    EditPaystubDialog,
    EditClientDialog,
    LogReferralDialog,
    EarlyBirdStatusTag,
    TransactionDialog,
    OfficeTag,
    W2PdfButton,
    RafSlipsPdfButton,
  },
  data() {
    return {
      loading: {
        client: false,
        earlyBird: false,
        paystubs: false,
        referrals: false,
      },
      isRemovingEnrollment: false,
      editPaystubDialogVm: {
        visible: false,
        paystub: new Paystub(),
      },
      editClientDialogVm: {
        visible: false,
        client: new Client(),
        isSpouse: false,
      },
      logReferralDialogVm: {
        visible: false,
        rafCode: null,
      },
      transactionDialogVm: {
        visible: false,
        transaction: null,
      },
    };
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler() {
        this.fetchClient();
        this.fetchClientEarlyBird();
        this.fetchClientPaystubs();
        this.fetchClientReferrals();
      },
    },
  },
  methods: {
    ...mapActions('Client', [
      'loadClient',
      'loadClientEarlyBird',
      'loadClientPaystubs',
      'loadClientReferrals',
      'setClient',
      'addReferral',
      'removeEBEnrollment',
      'changeReferralOffice',
      'overrideReferral',
      'removeReferral',
    ]),
    async fetchClient() {
      try {
        this.loading.client = true;
        await this.loadClient(this.clientId);
      } catch (e) {
        console.warn('client could not be loaded', e);
        this.$notify({
          type: 'error',
          title: 'Error',
          message: 'Could not load client',
        });
      } finally {
        this.loading.client = false;
      }
    },
    async fetchClientEarlyBird() {
      try {
        this.loading.earlyBird = true;
        await this.loadClientEarlyBird(this.clientId);
      } catch (e) {
        console.warn('client early bird state could not be loaded', e);
        this.$notify({
          type: 'error',
          title: 'Error',
          message: 'Could not load client Early Bird program status.',
        });
      } finally {
        this.loading.earlyBird = false;
      }
    },
    async fetchClientPaystubs() {
      try {
        this.loading.paystubs = true;
        await this.loadClientPaystubs(this.clientId);
      } catch (e) {
        console.warn('client paystubs could not be loaded', e);
        this.$notify({
          type: 'error',
          title: 'Error',
          message: 'Could not load client paystubs.',
        });
      } finally {
        this.loading.paystubs = false;
      }
    },
    async fetchClientReferrals() {
      try {
        this.loading.referrals = true;
        await this.loadClientReferrals(this.clientId);
      } catch (e) {
        console.warn('client referrals could not be loaded', e);
        this.$notify({
          type: 'error',
          title: 'Error',
          message: 'Could not load client referrals.',
        });
      } finally {
        this.loading.referrals = false;
      }
    },
    renderFullName(clientObj) {
      if (clientObj) {
        return `${get(clientObj, 'firstName', '')} ${get(clientObj, 'lastName', '')}`.trim();
      }
      return '';
    },
    formatDate(date) {
      return date ? moment(date).format('L LT') : '';
    },
    addPaystub() {
      this.editPaystubDialogVm.paystub = new Paystub();
      this.editPaystubDialogVm.visible = true;
    },
    editPaystub(paystub) {
      this.editPaystubDialogVm.paystub = paystub;
      this.editPaystubDialogVm.visible = true;
    },
    editPrimary() {
      this.editClientDialogVm.isSpouse = false;
      this.editClientDialogVm.client = new Client(this.client);
      this.editClientDialogVm.visible = true;
    },
    editSpouse() {
      this.editClientDialogVm.isSpouse = true;
      this.editClientDialogVm.client = this.spouse;
      this.editClientDialogVm.visible = true;
    },
    addSpouse() {
      this.editClientDialogVm.isSpouse = true;
      this.editClientDialogVm.client = new Client();
      this.editClientDialogVm.visible = true;
    },
    separateClientSpouse() {
      this.$confirm('Are you sure you want to separate the client and spouse?').then(() => {
        this.isLoading = true;
        api.marriedTo.removeMarriage(this.clientId).then(() => {
          this.fetchClient();
        }).finally(() => {
          this.isLoading = false;
        });
      }).catch(() => { });
    },
    removeEbOptout() {
      this.$confirm('Are you sure you want to remove the client\'s election to OPT-OUT? This will allow the client to complete an Early Bird Application.').then(() => {
        this.isLoading = true;
        api.ebOptout.remove(this.clientId).then(() => {
          this.fetchClientEarlyBird();
        }).finally(() => {
          this.isLoading = false;
        });
      }).catch(() => { });
    },
    displayLogReferralDialog() {
      this.logReferralDialogVm.rafCode = this.client.rafCode;
      this.logReferralDialogVm.visible = true;
    },
    navigateToPayReferrals() {
      this.$router.push({
        name: 'payClientReferral',
        params: {
          entityId: this.clientId,
        },
      });
    },
    navigateToEarlyBirdApplication() {
      this.$router.push({
        name: 'earlyBirdApplication',
        params: {
          clientId: this.clientId,
        },
      });
    },
    removeEnrollment() {
      const warningMessage = `
        Are you sure you want to remove this client's enrollment in the Early Bird Program?
        After the enrollment is removed, the client will have ability to enroll again at a later date.
        To prevent the ability to re-enroll, VOID the transaction.`;
      this.$confirm(warningMessage, 'Warning', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning',
      }).then(() => {
        this.isRemovingEnrollment = true;
        this.removeEBEnrollment().then(() => {
          this.$notify.success({
            title: 'Success',
            message: 'Enrollment Removed.',
            position: 'bottom-right',
          });
        }).catch(() => {
          this.$notify.error({
            title: 'Error',
            message: 'Could not remove Early Bird Enrollment.',
            position: 'bottom-right',
          });
        }).finally(() => {
          this.isRemovingEnrollment = false;
        });
      });
    },
    overrideApprovalForEB() {
      this.$confirm('Are you sure you want to manually approve this client\'s Early Bird Application?').then(() => {
        this.isLoading = true;
        api.ebQualification.overrideApproval(this.ebQualification).then(() => {
          this.fetchClientEarlyBird();
        }).finally(() => {
          this.isLoading = false;
        });
      }).catch(() => { });
    },
    viewTransaction(transaction) {
      this.transactionDialogVm.transaction = transaction;
      this.transactionDialogVm.visible = true;
    },
    onTransactionUpdate() {
      this.fetchClient();
      this.fetchClientEarlyBird();
      this.fetchClientPaystubs();
    },
    onAddedReferral(referral) {
      this.addReferral(referral);
    },
    onChangeReferralOffice(updateTO) {
      this.changeReferralOffice(updateTO).then(() => {
        this.$notify.success({
          title: 'Success',
          message: 'The office for the referral has been updated.',
          position: 'bottom-right',
        });
      }).catch((e) => {
        this.$notify.error({
          title: 'Error',
          message: get(e, 'response.data', 'An unexpected error occurred.'),
          position: 'bottom-right',
        });
      });
    },
    onOverrideReferral(fullReferral) {
      this.overrideReferral(fullReferral).then(() => {
        this.$notify.success({
          title: 'Success',
          message: 'The status and amount for the referral have been updated.',
          position: 'bottom-right',
        });
      }).catch((e) => {
        this.$notify.error({
          title: 'Error',
          message: get(e, 'response.data', 'An unexpected error occurred.'),
          position: 'bottom-right',
        });
      });
    },
    onDeleteReferral(referral) {
      this.removeReferral(referral).then(() => {
        this.$notify.success({
          title: 'Success',
          message: 'The referral has been deleted.',
          position: 'bottom-right',
        });
      }).catch((e) => {
        this.$notify.error({
          title: 'Error',
          message: get(e, 'response.data', 'An unexpected error occurred.'),
          position: 'bottom-right',
        });
      });
    },
  },
  computed: {
    ...mapGetters('Client', [
      'client',
      'spouse',
      'paystubs',
      'ebOptout',
      'referrals',
      'ebQualification',
      'ebEnrollment',
      'referrals',
      'ebApplicationComplete',
      'ebApplicationEnrolled',
      'hasReferralsReadyForPayout',
    ]),
    ...mapGetters('User', [
      'isAdmin',
    ]),
    clientId() {
      return parseInt(this.$route.params.id, 10);
    },
    formattedReasonsDNQ() {
      if (this.isAdmin) {
        const reasons = get(this.ebQualification, 'reasonsDeclined', []);
        if (reasons.length > 0) {
          return `Reasons Declined: ${reasons.join(', ')}`;
        }
      }
      return '';
    },
    fullName() {
      return this.renderFullName(this.client);
    },
    spouseName() {
      return this.renderFullName(this.spouse);
    },
    address() {
      if (this.client) {
        const address = this.client.address || '';
        const city = this.client.city || '';
        const state = this.client.state || '';
        const zip = this.client.zip || '';

        let addressString = '';
        if (address.length) {
          addressString += `${address}\n`;
        }
        if (city.length) {
          addressString += city + (state.length ? ', ' : '');
        }

        addressString += `${state} ${zip}`;
        return addressString;
      }
      return '';
    },
    dateAdded() {
      return this.formatDate(this.client.dateAdded);
    },
    spouseDateAdded() {
      return this.formatDate(this.spouse.dateAdded);
    },
    hasSpouse() {
      return this.spouse && this.spouse.id;
    },
  },
};

</script>

<style lang="scss" scoped>
.disable-spouse {
  background: #F2F2F2;
}

.quick-info-box {
  p {
    font-weight: bold;
  }
}

.detail-header {
  font-size: 18px;
  font-weight: bold;
  line-height: 24px;

  &.referral span {
    font-weight: normal;
    font-size: 0.8em;
  }

  button {
    float: right;
    padding: 5px 5px;
  }
}
</style>
